import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useId, useState } from "react";

import { Button } from "./Button";
import { cx } from "./cx";
import { Select } from "./Select";

/** Rows per page the person can choose from. */
export const PAGE_SIZES = [20, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export const DEFAULT_PAGE_SIZE: PageSize = 20;

interface PagerState {
  /** What the list was filtered by when this state was made: a different one starts again at page one. */
  forKey: string;
  size: PageSize;
  /** `tokens[i]` is the page token that fetches page `i` (null for the first). */
  tokens: (string | null)[];
}

export interface CursorPager {
  pageSize: PageSize;
  /** Zero-based number of the page on screen. */
  index: number;
  /** Send as `page_token`; null on the first page. */
  token: string | null;
  hasPrevious: boolean;
  /** Go to the next page, whose token the current page answered with. */
  next: (nextToken: string) => void;
  previous: () => void;
  setPageSize: (size: PageSize) => void;
}

/**
 * Where a person is in a list the server pages with opaque cursors. Cursors can't jump to a page number, so the
 * tokens already seen are kept and Previous steps back through them. `filterKey` should change whenever the
 * filters, search or sort do: the list then starts again at the first page. Page size survives that.
 */
export function useCursorPager(filterKey: string, initialSize: PageSize = DEFAULT_PAGE_SIZE): CursorPager {
  const [state, setState] = useState<PagerState>({ forKey: filterKey, size: initialSize, tokens: [null] });
  // A stale state (made for other filters) is read as a fresh first page, and replaced on the next move.
  const current: PagerState =
    state.forKey === filterKey ? state : { ...state, forKey: filterKey, tokens: [null] };
  const index = current.tokens.length - 1;
  return {
    pageSize: current.size,
    index,
    token: current.tokens[index] ?? null,
    hasPrevious: index > 0,
    next: (nextToken) => setState({ ...current, tokens: [...current.tokens, nextToken] }),
    previous: () => setState({ ...current, tokens: current.tokens.slice(0, -1) }),
    setPageSize: (size) => setState({ forKey: filterKey, size, tokens: [null] }),
  };
}

/** Warm the cache with the page after this one, so Next shows at once. Only while that page's token is known. */
export function usePrefetchNext<T>(
  nextToken: string | null | undefined,
  queryKey: (token: string) => QueryKey,
  queryFn: (token: string) => Promise<T>,
) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!nextToken) return;
    // Settles on its own; a failed prefetch is simply not cached, and Next fetches the page normally.
    void qc.prefetchQuery({
      queryKey: queryKey(nextToken),
      queryFn: () => queryFn(nextToken),
      staleTime: 30_000,
    });
    // The key and the fetcher are rebuilt on every render; the token is what identifies the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextToken, qc]);
}

const number = new Intl.NumberFormat("en-GB");

/**
 * "Showing 21–40 of 1,204", Previous / Next and a page-size picker: the one pager the portal's long lists share.
 * `total` is the number of rows matching the current filters, across every page; `shown` is how many are on screen.
 */
export function Pager({
  pager,
  shown,
  total,
  hasNext,
  onNext,
  loading = false,
  noun = "results",
  className,
}: {
  pager: CursorPager;
  shown: number;
  total: number | undefined;
  hasNext: boolean;
  onNext: () => void;
  /** A page is being fetched: the controls wait for it. */
  loading?: boolean;
  /** What the rows are, for the empty message and the labels ("invoices"). */
  noun?: string;
  className?: string;
}) {
  const sizeId = useId();
  const first = pager.index * pager.pageSize + 1;
  const range =
    total === undefined
      ? ""
      : shown === 0
        ? `No ${noun}`
        : `Showing ${number.format(first)}–${number.format(first + shown - 1)} of ${number.format(total)}`;
  return (
    <nav
      aria-label={`${noun} pages`}
      className={cx("flex flex-wrap items-center gap-x-4 gap-y-2", className)}
    >
      <p className="text-xs text-fg-subtle" aria-live="polite">
        {range}
      </p>
      <span className="flex-1" />
      <label
        htmlFor={sizeId}
        className="flex shrink-0 items-center gap-2 whitespace-nowrap text-xs text-fg-subtle"
      >
        Rows per page
        {/* `!`: the shared Select is `w-full` and `py-2`, and class names are not merged. */}
        <Select
          id={sizeId}
          value={pager.pageSize}
          onChange={(e) => pager.setPageSize(Number(e.target.value) as PageSize)}
          className="h-8 w-auto! shrink-0 py-1! text-[13px]"
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Select>
      </label>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabledReason={
            loading ? "Loading this page…" : !pager.hasPrevious ? "You're on the first page" : undefined
          }
          onClick={pager.previous}
          icon={<ChevronLeft className="size-4" aria-hidden />}
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabledReason={loading ? "Loading this page…" : !hasNext ? "You're on the last page" : undefined}
          onClick={onNext}
        >
          Next
          <ChevronRight className="size-4" aria-hidden />
        </Button>
      </div>
    </nav>
  );
}
